<script setup>
import { ref, inject, onMounted } from 'vue'
import axios from 'axios'
import { useUserStore } from '@/stores/UserStore'

const siteConfig = inject('siteConfig')
const formData = ref(null)
const userStore = useUserStore()
const props = defineProps(['formId', 'relatedId', 'handleFormData', 'submitOnChange', 'requireAll', 'backTo'])
const formRef = ref(null)

function mapFormData(inputData) {
  const schema = {
    title: {
      type: 'static',
      content: inputData.qTitle,
      tag: 'h3',
    },
    description: {
      type: 'static',
      content: inputData.qdescr,
      tag: 'p',
    },
    divider: {
      type: 'static',
      tag: 'hr',
    },
  }

  inputData.rows.forEach(row => {
    schema[row.rowName] = mapRowToField(row)
  })

  if (!props.submitOnChange) {
    schema.button = { type: 'button', buttonLabel: inputData.qButtonText || 'Submit', submits: true }
  }

  return {
    schema: schema,
    data: {
      userId: userStore?.account?.personId || '42',
      formId: inputData.formId,
      qkey: inputData.qkey,
    },
  }
}

function getBaseField(row) {
  return {
    ...mapDataTypeToFieldType(row),
    label: row.rowLabel,
    placeholder: '',
    native: false,
    rules: row.required || props.requireAll ? ["required"] : [],
  };
}

function setFieldItems(baseField, row) {
  baseField.items = row.controlContent?.split(',')?.map(option => option.trim()) || [];

  if (baseField.type === 'multiselect' || baseField.type === 'checkboxgroup') {
    baseField.default = Array.isArray(row.rowValue) ? row.rowValue : [];
  } else {
    baseField.default = row.rowValue;
  }

  if (baseField.type === 'select') {
    baseField.multiple = false;
  } else if (baseField.type === 'multiselect') {
    baseField.multiple = true;
  }
}

function mapRowToField(row) {
  const baseField = getBaseField(row);

  if (['select', 'multiselect', 'checkboxgroup'].includes(baseField.type)) {
    setFieldItems(baseField, row);
  } else {
    baseField.default = row.rowValue;
  }

  return baseField;
}

const controlTypeMap = {
  BOOLENBOX: { type: 'checkbox' },
  LISTBOX: { type: 'multiselect', native: false },
  SELECT: { type: 'select', native: false },
  CHECKBOXES: { type: 'checkboxgroup', native: false },
};

const rowDataTypeMap = {
  decimal: { type: 'text', inputType: 'number', rules: ['numeric'] },
  boolean: { type: 'checkbox' },
  integer: { type: 'text', inputType: 'number', rules: ['numeric'] },
};

function mapDataTypeToFieldType(data) {
  return controlTypeMap[data.controlType] || rowDataTypeMap[data.rowDataType] || { type: 'text' };
}

onMounted(async () => {
  const response = await axios.get(`${siteConfig.apiBaseUrl}/questionnaire/activequestionnaires/${props.formId}`)
  if (response.data.ok) {
    formData.value = mapFormData(response.data.data)
  } else {
    // router.push('/not-found')
  }
})

// Coerce values to the correct type from string to integer, decimal, boolean, etc.
const coerceValue = (value, type) => {
  if (!value) {
    return null
  }
  if (Array.isArray(value)) {
    return value.map(v => coerceValue(v, type)).join(',')
  } else if (type === 'integer') {
    return parseInt(value)
  } else if (type === 'decimal') {
    return parseFloat(value)
  } else if (type === 'boolean') {
    return value.toString()
  }
  return value
}

const keyToType = (key) => {
  if (key.startsWith('d')) {
    return 'decimal'
  } else if (key.startsWith('b')) {
    return 'boolean'
  } else if (key.startsWith('i')) {
    return 'integer'
  }
  // t, lt
  return 'text'
}

const formatResponse = (data) => {
  if (!data) {
    return null
  }

  const rows = Object.entries(data).map(([key, value]) => {
    let rowDataType = keyToType(key)
    value = coerceValue(value, rowDataType)

    return {
      rowName: key,
      rowDataType: rowDataType,
      rowValue: value
    }
  })

  return {
    questionnairews: {
      formId: formData.value.data.formId,
      relatedId: props.relatedId,
      qkey: formData.value.data.qkey,
      userId: userStore.account?.personId,
      rows: rows,
    },
  }
}

const handleSuccess = (response, form$) => {
  form$.messageBag.clear() // clear message bag
  form$.messageBag.append('Form submitted', 'message') // add success message
  form$.reset() // reset form data
}

const handleError = (error, details, form$) => {
  form$.messageBag.clear() // clear message bag

  switch (details.type) {
    // Error occured while preparing elements (no submit happened)
    case 'prepare':
      console.log(error) // Error object

      form$.messageBag.append('Could not prepare form')
      break

    // Error occured because response status is outside of 2xx
    case 'submit':
      console.log(error) // AxiosError object
      console.log(error.response) // axios response
      console.log(error.response.status) // HTTP status code
      console.log(error.response.data) // response data

      form$.messageBag.append('Some error from the backend')
      break

    // Request cancelled (no response object)
    case 'cancel':
      console.log(error) // Error object

      form$.messageBag.append('Request cancelled')
      break

    // Some other errors happened (no response object)
    case 'other':
      console.log(error) // Error object

      form$.messageBag.append('Couldn\'t submit form')
      break
  }
}

const submitQuestion = async (_FormData, form$, valid = false) => {
  const data = formatResponse(form$?.data)

  if (props.handleFormData) {
    props.handleFormData(data, valid)
  } else if (data) {
    form$.cancelToken = form$.$vueform.services.axios.CancelToken.source()
    return await form$.$vueform.services.axios.post(`${siteConfig.apiBaseUrl}/questionnaire/savequestionnaire`,
      data /* | data | requestData */,
      {
        cancelToken: form$.cancelToken.token,
      },
    )
  }
}

const handleChange = () => {
  if (props.submitOnChange) {
    setTimeout(() => {
      submitQuestion(null, formRef.value, formRef.value?.validated && !formRef.value?.hasErrors)
    }, 100)
  }
}

</script>

<template>
  <section>
    <div v-if="!userStore.account?.personId">
      <p class="text-lg font-semibold mb-4">You must be signed in.</p>
      <div class="">
        <router-link :to="`/sign-in?backTo=${backTo || '/forms/' + formId}`" class="outlined-accent-1 sign-link body-regular lg:small-regular px-4 py-2">Sign In</router-link>
      </div>
    </div>
    <section v-else>
      <Vueform
        ref="formRef" v-if="formData" v-bind="formData" :endpoint="submitQuestion" @success="handleSuccess"
        @change="handleChange"
        @error="handleError"
        validate-on="change"
      />
    </section>
  </section>
</template>